import React, { useEffect, useRef, useState } from "react";
import {
  Background,
  ReactFlow,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  MarkerType,
} from "@xyflow/react";
import {
  Box,
  Flex,
  Button,
  HStack,
  Text,
  VStack,
  IconButton,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Center,
  Spinner,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip
} from "@chakra-ui/react";
import { EditIcon, CheckIcon } from "@chakra-ui/icons";
import {
  Header,
  MetricProto,
} from "build-ts/metadata";
import {
  MetadataTypeEnum,
  metadataTypeEnumFromJSON,
  metadataTypeEnumToJSON,
} from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { getMetricById, listMetrics } from "services/MetricService";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import {
  KGObservationProto,
  selectionTypeToJSON,
} from "build-ts/analysis_plan";
import {
  getKGGrowthBenchmarks,
  mergeMetricEdgesByType,
} from "utils/analysisUtils";
import { ObservationUtils } from "utils/ObservationUtils";
import { useURLContext } from "context/URLContext";
import { searchPlansheaders } from "services/AnalysisPlanServices";
import { formatToSentenceCase } from "utils/nameUtils";
import AnalysisStepNode from "./AnalysisStepNode";
import { StepInterface } from "./Interface";
import ListPlans from "./ListPlans";
import MetricsSelection from "components/analyse/MetricsSelection";
import { getScheduledJob, runJobAsync } from "services/ScheduleServices";
import JobRuns from "containers/schedule/JobRuns";
import { ScheduledJobProto } from "build-ts/schedule";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import lodash from "lodash";
import { TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";
import { FaHistory, FaPlay, FaRegLightbulb, FaSave, FaTimes } from "react-icons/fa";
import { BsFillInfoSquareFill } from "react-icons/bs";
import NodeConfigurator from "./NodeConfigurator";
import { LuWorkflow } from "react-icons/lu";
import { MdSchedule } from "react-icons/md";
import ScheduledJob from "containers/schedule/ScheduledJob";
import PlanQNA from "./PlanQNA";
import { populateRecommendationEdits } from "utils/analysisPlanUtils";
import CustomModal from "components/modals/CustomModal";
import PlanDescriptionComponent from "./PlanDescriptionComponent";
import DataTableComponent from "./DataTableComponent";

// Node dimensions and spacing constants
const NODE_HEIGHT = 126;
const NODE_WIDTH = 216;
const MIN_HORIZONTAL_SPACING = 350; // Minimum horizontal spacing between levels
const MIN_VERTICAL_SPACING = 20;   // Minimum vertical spacing between siblings
const VERTICAL_PADDING = 10;        // Extra padding to prevent overlap

interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
  bounds: { minY: number; maxY: number; width: number; height: number };
}

// Helper function to calculate the total height needed for a subtree
const calculateSubtreeHeight = (
  nodeId: string,
  nodeMap: Map<string, StepInterface>,
  processedNodeIds = new Set<string>(),
): number => {
  const node = nodeMap.get(nodeId);
  if (!node || processedNodeIds.has(nodeId)) {
    return NODE_HEIGHT;
  }

  processedNodeIds.add(nodeId);

  if (!node.recommendation_rules || !node.recommendation_children_map) {
    return NODE_HEIGHT;
  }

  let totalChildrenHeight = 0;
  let childCount = 0;

  // Calculate height needed for all children
  for (const [, childIds] of Array.from(node.recommendation_children_map.entries())) {
    for (const childId of childIds) {
      const childHeight = calculateSubtreeHeight(childId, nodeMap, new Set(processedNodeIds));
      totalChildrenHeight += childHeight;
      childCount++;
    }
  }

  // Add spacing between children
  const spacingHeight = Math.max(0, childCount - 1) * (MIN_VERTICAL_SPACING + VERTICAL_PADDING);
  
  // Return the maximum of: node height, or total children height + spacing
  return Math.max(NODE_HEIGHT, totalChildrenHeight + spacingHeight);
};

// LayoutTree function that uses the nodeMap and recommendation_children_map
const layoutTree = (
  nodeId: string,
  nodeMap: Map<string, StepInterface>,
  x: number,
  y: number,
  hoveredEdgeId: string | null,
  processedNodeIds = new Set<string>(),
): LayoutResult => {
  const node = nodeMap.get(nodeId);
  if (!node || processedNodeIds.has(nodeId)) {
    return { 
      nodes: [], 
      edges: [], 
      bounds: { minY: y, maxY: y, width: 0, height: 0 } 
    };
  }

  // Mark this node as processed to avoid cycles
  processedNodeIds.add(nodeId);

  let nodes: Node[] = [];
  let edges: Edge[] = [];
  let minY = y;
  let maxY = y;

  // Add this node
  nodes.push({
    id: node.id,
    type: "customNode",
    position: { x, y },
    // TODO: Fix this type cast.
    data: node as unknown as Record<string, unknown>,
  });

  // Process all child nodes from recommendations
  if (node.recommendation_rules && node.recommendation_children_map) {
    // Collect all children and their required heights
    const childrenInfo: Array<{
      childId: string;
      recommendationIndex: number;
      requiredHeight: number;
    }> = [];

    for (const [recommendationIndex, childIds] of Array.from(
      node.recommendation_children_map.entries()
    )) {
      for (const childId of childIds) {
        const requiredHeight = calculateSubtreeHeight(childId, nodeMap);
        childrenInfo.push({
          childId,
          recommendationIndex,
          requiredHeight,
        });
      }
    }

    if (childrenInfo.length > 0) {
      // Calculate total height needed for all children
      const totalRequiredHeight = childrenInfo.reduce(
        (sum, child) => sum + child.requiredHeight,
        0
      );
      
      // Calculate dynamic vertical spacing based on available space and number of children
      const baseSpacing = MIN_VERTICAL_SPACING + VERTICAL_PADDING;
      const totalSpacing = Math.max(0, childrenInfo.length - 1) * baseSpacing;
      const totalNeededHeight = totalRequiredHeight + totalSpacing;

      // Start positioning children from the center, spreading them out
      let currentY = y - totalNeededHeight / 2;

      // Process each child with calculated positions
      for (let i = 0; i < childrenInfo.length; i++) {
        const { childId, recommendationIndex, requiredHeight } = childrenInfo[i];
        
        // Position child at the center of its allocated space
        const childCenterY = currentY + requiredHeight / 2;
        const childX = x + MIN_HORIZONTAL_SPACING;

        // Layout the child node and its descendants
        const childLayout = layoutTree(
          childId,
          nodeMap,
          childX,
          childCenterY,
          hoveredEdgeId,
          new Set(processedNodeIds),
        );

        // Add the child's nodes and edges
        nodes = nodes.concat(childLayout.nodes);
        edges = edges.concat(childLayout.edges);

        // Update bounds
        minY = Math.min(minY, childLayout.bounds.minY);
        maxY = Math.max(maxY, childLayout.bounds.maxY);

        // Create an edge from the parent to the child
        edges.push({
          id: `e-${node.id}-${childId}`,
          source: node.id,
          target: childId,
          animated: true,
          label: hoveredEdgeId === `e-${node.id}-${childId}` ? 
            (node.recommendation_rules[recommendationIndex]?.selections[0]?.type ? 
              formatToSentenceCase(selectionTypeToJSON(node?.recommendation_rules[recommendationIndex]?.selections[0]?.type!)) : "") : "",
          style: { stroke: "#4e1e6c" },
          labelStyle: { 
            fontSize: "12px",
            fontWeight: "semibold",
          },
          labelBgStyle: {
            fill: "#b794e1", // Background color
            fillOpacity: 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "#4e1e6c",
            width: 33,
            height: 33,
          },
        });

        // Move to next child position
        currentY += requiredHeight + baseSpacing;
      }
    }
  }

  // Update bounds to include this node
  minY = Math.min(minY, y - NODE_HEIGHT / 2);
  maxY = Math.max(maxY, y + NODE_HEIGHT / 2);

  return { 
    nodes, 
    edges, 
    bounds: { 
      minY, 
      maxY, 
      width: NODE_WIDTH, 
      height: maxY - minY 
    } 
  };
};

const AnalysisPlan: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { metadataId, metadataType } = useURLContext();
  const {
    metrics,
    setMetrics,
    selectedMetricHeader,
    metricList,
    setSelectedMetricHeader,
    plan,
    setPlan,
    nodeMap,
    setNodeMap,
    setSelectedNodeId,
    setMetricList,
    saveKGAnalysisPlanHandler,
    setObservationData,
    questions,
    setQuestions,
    currentPlanHeader,
    setCurrentPlanHeader,
    updateHeader,
    handleLoadPlan,
    isPlanChanged,
    setIsPlanChanged,
    originalNodeMap,
    setOriginalNodeMap,
    originalQuestions,
    setOriginalQuestions,
    isFetchingPlan,
    configurator,
    observationData,
    setShowRecommendedFilters
  } = useAnalysisPlanContext();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [planHeaders, setPlanHeaders] = useState<Header[]>([]);
  const [selectedPlanHeaders, setSelectedPlanHeaders] = useState<Header[]>([]);
  // Add editing state for plan name
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editingName, setEditingName] = useState<string>("");
  const [isCreatingNewPlan, setIsCreatingNewPlan] = useState<boolean>(false);
  const [scheduledJobData, setScheduledJobData] = useState<ScheduledJobProto | null>(null);
  const [isLoadingPlanRuns, setIsLoadingPlanRuns] = useState(false);
  // Tab state management
  const [tabIndex, setTabIndex] = useState(0);
  
  // Add sidebar visibility state
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(true);
  
  // Modal state for description
  const { 
    isOpen: isDescriptionModalOpen, 
    onOpen: onDescriptionModalOpen, 
    onClose: onDescriptionModalClose 
  } = useDisclosure();

  // Metrics selection modal disclosure
  const { 
    isOpen: isMetricsSelectionOpen, 
    onOpen: onMetricsSelectionOpen, 
    onClose: onMetricsSelectionClose 
  } = useDisclosure();

  // Handle description modal open
  const handleDescriptionModalOpen = () => {
    if (!currentPlanHeader?.id) return;
    onDescriptionModalOpen();
  };

  // Handle description modal close
  const handleDescriptionModalClose = () => {
    onDescriptionModalClose();
  };

  // Toggle function
  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };


  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Function to check if plan has changes
  const checkForPlanChanges = () => {
    const nodeMapChanged = !lodash.isEqual(nodeMap, originalNodeMap);

    // Compare questions
    const questionsChanged = !lodash.isEqual(questions, originalQuestions);
    
    const hasChanges = nodeMapChanged || questionsChanged;
    
    return hasChanges;
  };

  // Track changes in plan data
  useEffect(() => {
    const hasChanges = checkForPlanChanges();
    setIsPlanChanged(hasChanges);
  }, [nodeMap, questions, originalNodeMap, originalQuestions]);

  // Update original state for change tracking when a plan is loaded via context
  useEffect(() => {
    if (currentPlanHeader?.created_date_ms && nodeMap.size > 0 && originalNodeMap.size === 0) {
      // This is a loaded plan and we haven't set original state yet
      setOriginalNodeMap(new Map(Array.from(nodeMap.entries()).map(([key, value]) => 
        [key, lodash.cloneDeep(value)])));
      setOriginalQuestions([...questions]);
      setIsPlanChanged(false);
    }
  }, [currentPlanHeader, nodeMap, questions, originalNodeMap]);

  useEffect(() => {
    const fetchMetrics = async () => {
      const metrics = await listMetrics("" /* Model ID */);
      setMetricList(metrics);
    };
    fetchMetrics();
    fetchPlanHeaders();
    
    // empty all the plan state when the user navigates back to the plan page from a different page
    setSelectedPlanHeaders([]);
    setPlan(null);
    setSelectedNodeId(null);
    setCurrentPlanHeader(undefined);
    setNodeMap(new Map());
    setQuestions([]);
  }, []);

  useEffect(() => {
    if (
      metadataId &&
      metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.ANALYSIS_PLAN &&
      metricList.length > 0 // Ensure metric list is loaded first
    ) {
      handleLoadPlan(metadataId);
    }
  }, [metadataId, metadataType, metricList]);

  // Update React Flow nodes and edges whenever the tree or nodeMap changes
  useEffect(() => {
    if (plan && nodeMap.size > 0) {
      const layout = layoutTree(plan.id, nodeMap, 50, 200, hoveredEdgeId);
      setNodes(layout.nodes);
      setEdges(layout.edges);
    } else {
      // Clear nodes and edges if there's no tree or nodeMap is empty
      setNodes([]);
      setEdges([]);
    }
  }, [plan, nodeMap, setNodes, setEdges, hoveredEdgeId]);

  // handle plan selection from url params.
  useEffect(() => {
    const handlePlanSelectionFromUrl = async () => {
      // Only load plan from URL if:
      // 1. There's an ID in the URL.
      // 2. Plan headers are loaded.
      // 3. No current plan is loaded OR the current plan ID doesn't match the URL ID.
      if(id && planHeaders.length > 0 && (!currentPlanHeader || currentPlanHeader.id !== id)){
        const planHeader = planHeaders.find(header => header.id === id);
        if(planHeader){
          setSelectedPlanHeaders([planHeader]);
          await handleLoadPlan(planHeader.id!);
          setIsSidebarVisible(false);
          // Update original state for change tracking after loading
          if (nodeMap.size > 0) {
            setOriginalNodeMap(new Map(Array.from(nodeMap.entries()).map(([key, value]) => 
              [key, lodash.cloneDeep(value)])));
            setOriginalQuestions([...questions]);
            setIsPlanChanged(false);
          }
        }
      }
    };
    handlePlanSelectionFromUrl();
  }, [id, planHeaders]);

  // useEffect to handle plan runs during tab change
  useEffect(() => {
    if(location.pathname.includes("/runs")){
      setTabIndex(2);
      handleShowPlanRuns();
    } else if(location.pathname.includes("/insights")){
      setTabIndex(1);
    } else if(location.pathname.includes("/schedule")){
      setTabIndex(3);
    } else {
      setTabIndex(0);
    }
  }, [location.pathname, currentPlanHeader]);

  const fetchPlanHeaders = async () => {
    setIsLoading(true);
    const headers = await searchPlansheaders(
      -1 /* pageSize*/, 
      1 /*pageNumber*/, 
      "LAST_MODIFIED" /*sortByField*/, 
      false /*sortAscending*/, 
      "" /*searchString*/, 
      [] /*metricIds*/,
      true /*includeScheduleActivity*/,
      ""
    );
    if (headers) {
      setPlanHeaders(headers?.headers || []);
    } else {
      setPlanHeaders([]);
    }
    setIsLoading(false);
  };

  // Loading state for Metric change.
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Loading state for KG Plan execution.
  const [isExecutingPlan, setIsExecutingPlan] = useState<boolean>(false);
  // Helper function to fetch metric and model data
  const fetchMetric = async (
    metricId: string
  ): Promise<{ metricData: MetricProto | null }> => {
    // Fetch MKG.
    const metricData = await getMetricById(metricId, true /* Include edges */);
    if (!metricData || !metricData?.header) {
      showToast(`Failed to fetch metric with ID: ${metricId}`, "", "error");
      return { metricData: null };
    }

    // Update metrics map
    const metricsMap = new Map<string, MetricProto>(metrics);
    metricsMap.set(metricId, metricData);
    setMetrics(metricsMap);

    return { metricData };
  };

  // Handle plan selection from the list
  const handlePlanSelection = async (planHeader: Header): Promise<void> => {
    if (!planHeader.id) {
      showToast("Invalid plan selected", "", "error");
      return;
    }
    setSelectedPlanHeaders([planHeader]);
    setObservationData(null);
    setIsSidebarVisible(false);
    // Only load the plan if it's not already loaded or is a different plan.
    if (!currentPlanHeader || currentPlanHeader.id !== planHeader.id) {
      await handleLoadPlan(planHeader.id!);
    }
    if(planHeader.id){
      navigate(`/plan/${planHeader.id}`, { replace: true });
      setTabIndex(0);
    }
    // Close the node configurator when switching plans
    if(configurator.isOpen){
      configurator.onClose();
    }
    if(isCreatingNewPlan){
      setIsCreatingNewPlan(false);
    }
  };

  // Handle create new plan
  const handleCreateNewPlan = async (planName: string): Promise<void> => {
    // Create a new plan header with basic info
    const newPlanHeader = Header.create({
      id: uuidv4(),
      name: planName,
      description: "",
      type: MetadataTypeEnum.ANALYSIS_PLAN
    });

    // Clear the right canvas - reset all plan state
    setPlan(null);
    setNodeMap(new Map());
    setSelectedMetricHeader([]);
    setQuestions([]);
    setObservationData(null);
    setSelectedPlanHeaders([]);
    
    // Clear original state for new plan
    setOriginalNodeMap(new Map());
    setOriginalQuestions([]);
    setIsPlanChanged(false);
    
    // Set the new plan as current to able to use context functions to create the plan steps.
    setCurrentPlanHeader(newPlanHeader);
    setIsCreatingNewPlan(true);
    navigate("/plan");

    // Close the node configurator when creating a new plan
    if(configurator.isOpen){
      configurator.onClose();
    }
    
    // Open metrics selection modal
    onMetricsSelectionOpen();
    
    showToast(`New plan "${planName}" created successfully`, "", "success");
  };

  // Modified to handle multiple metrics
  const createRootNode = (metricDataArray: MetricProto[]): StepInterface => {
    if (!metricDataArray || metricDataArray.length === 0) {
      throw new Error("No metrics provided to create root node");
    }
    // Process multiple metric edges using our utility function
    const processedEdges = mergeMetricEdgesByType(metricDataArray);
    // Convert headers to ObservationColumnProto objects
    const attributeColumns = ObservationUtils.getObservationColumns(
      processedEdges?.attributeHeaders || []
    );
    const measureColumns = ObservationUtils.getObservationColumns(
      processedEdges?.measureHeaders || []
    );
    const temporalColumns = ObservationUtils.getObservationColumns(
      processedEdges?.temporalHeaders || []
    );
    const metricColumns = ObservationUtils.getObservationColumns(
      processedEdges?.metricEdgeHeaders || []
    );
    // Extract metrics from metric columns
    const availableMetrics: MetricProto[] = [];
    metricColumns.forEach((col) => {
      if (col.kg_column_header?.type === MetadataTypeEnum.METRIC) {
        // Create a MetricProto from the column header
        const metric = MetricProto.create({
          header: col.kg_column_header,
        });
        availableMetrics.push(metric);
      }
    });

    // Don't include metric columns in non_benchmarks - they should be handled separately
    const nonBenchmarks = [
      ...attributeColumns,
      ...measureColumns,
      ...temporalColumns,
    ];

    // Get growth benchmarks
    const growthBenchmarks = getKGGrowthBenchmarks([]);
    // Combine with benchmark entities from metrics
    const allBenchmarks = [
      ...(processedEdges?.benchmarkEntities || []),
      ...growthBenchmarks,
    ];

    // Create metrics array with all selected metrics (initially selected from the input)
    const selectedMetricsArray = metricDataArray.filter(Boolean);

    // Combine available metrics: input metrics + any additional metrics from edges
    const allAvailableMetrics = [...selectedMetricsArray];
    availableMetrics.forEach((metric) => {
      // Add if not already present
      if (
        !allAvailableMetrics.some(
          (existing) => existing.header?.id === metric.header?.id
        )
      ) {
        allAvailableMetrics.push(metric);
      }
    });

    // Determine node name (use first metric if multiple selected)
    const primaryMetric = metricDataArray[0];

    // Create KG observation for the node
    const kgObservation = KGObservationProto.create({
      metrics: [...selectedMetricsArray],
      benchmarks: [],
      filters: [],
      columns: [],
    });

    // Create and return the root node
    return {
      id: uuidv4(),
      label:
        metricDataArray.length === 1
          ? primaryMetric?.header?.name || "Root Metric"
          : `Combined Metrics (${metricDataArray.length})`,
      type: "metric",
      metric_name:
        metricDataArray.length === 1
          ? primaryMetric?.header?.name || ""
          : `${metricDataArray.length} Metrics`,
      // All available metrics that can be selected
      metrics: [...allAvailableMetrics],
      // Currently selected metrics
      selected_metrics: [...selectedMetricsArray],

      // Available options for UI selection
      benchmarks: allBenchmarks,
      non_benchmarks: nonBenchmarks,
      temporal_columns: temporalColumns,

      // Initialize empty selections
      selected_columns: [],
      selected_benchmarks: [],
      selected_filters: [],

      // No parent for root node
      parent_node_id: undefined,

      // Initialize analysis columns array
      analysis_columns: [],

      // Initialize with empty recommendation rules and maps
      recommendation_rules: [],
      recommendation_children_map: new Map(),
      recommendation_edits_map: new Map(),
      recommendation_edits: [],

      // Set the KG observation
      kg_observation: kgObservation,
    };
  };

  // Handle metric selection from dropdown - updated for multi-select
  const handleMetricChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    metricId: string
  ) => {
    const isChecked = event.target.checked;

    // Update selected metrics in context
    if (isChecked) {
      // Add this metric
      setIsLoading(true);

      // Fetch the metric data
      const { metricData } = await fetchMetric(metricId);

      if (metricData) {
        // Get existing selected metrics
        const selectedMetrics = Array.from(metrics.values()).filter(
          (m) =>
            m.header && selectedMetricHeader.some((h) => h.id === m.header?.id)
        );

        // Add this metric to the list
        if (
          metricData &&
          !selectedMetrics.some((m) => m.header?.id === metricData.header?.id)
        ) {
          selectedMetrics.push(metricData);
        }

        // Set all selected metric headers
        const updatedHeaders = selectedMetrics
          .map((m) => m.header)
          .filter(Boolean) as Header[];
        setSelectedMetricHeader(updatedHeaders);

        // If this is the first selection, initialize the plan
        if (selectedMetrics.length > 0) {
          const rootNode = createRootNode(selectedMetrics);
          setPlan(rootNode);
          const newNodeMap = new Map<string, StepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
          
          // Store original state if it's a new plan being created
          if (isCreatingNewPlan) {
            setOriginalNodeMap(new Map([[rootNode.id, lodash.cloneDeep(rootNode)]]));
            setOriginalQuestions([]);
            setIsPlanChanged(false);
          }
        }
      }

      setIsLoading(false);
    } else {
      // Remove this metric
      const currentSelectedHeaders = selectedMetricHeader || [];
      const updatedHeaders = currentSelectedHeaders.filter(
        (header) => header.id !== metricId
      );
      setSelectedMetricHeader(updatedHeaders);

      // If there are still selected metrics, update the plan
      if (updatedHeaders.length > 0) {
        const selectedMetrics = Array.from(metrics.values()).filter(
          (m) => m.header && updatedHeaders.some((h) => h.id === m.header?.id)
        );

        if (selectedMetrics.length > 0) {
          const rootNode = createRootNode(selectedMetrics);
          setPlan(rootNode);
          const newNodeMap = new Map<string, StepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
          
          // Store original state if it's a new plan being created
          if (isCreatingNewPlan) {
            setOriginalNodeMap(new Map([[rootNode.id, lodash.cloneDeep(rootNode)]]));
            setOriginalQuestions([]);
            setIsPlanChanged(false);
          }
        }
      } else {
        // If no metrics are selected, clear the plan
        setPlan(null);
        setNodeMap(new Map());
        
        // Clear original state if it's a new plan being created
        if (isCreatingNewPlan) {
          setOriginalNodeMap(new Map());
          setOriginalQuestions([]);
          setIsPlanChanged(false);
        }
      }
    }
  };

  // // Execute the KG analysis plan
  // const handleExecutePlan = async () => {
  //   try {
  //     setIsExecutingPlan(true);
  //     await executeKGAnalysisPlanHandler();
  //   } catch (error) {
  //     console.error("Error executing KG analysis plan:", error);
  //   } finally {
  //     setIsExecutingPlan(false);
  //   }
  // };

  // Handle run job
  const handleRunJob = async () => {
    if (!currentPlanHeader?.created_date_ms) return;

    setIsExecutingPlan(true);
    try {
      await runJobAsync(currentPlanHeader?.id!, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYSIS_PLAN));
      showToast(
        "Job is running",
        "The scheduled job has been triggered and is now running.",
        "success"
      );
    } catch (error) {
      console.error("Error running job:", error);
      showToast("Failed to run job", "", "error");
    } finally {
      setIsExecutingPlan(false);
    }
  };

  // Handle show plan runs
  const handleShowPlanRuns = async () => {
    if (!currentPlanHeader || !currentPlanHeader.id) return;
    
    setIsLoadingPlanRuns(true);
    try {
      const scheduledJob = await getScheduledJob(currentPlanHeader.id, true);
      setScheduledJobData(scheduledJob || null);
    } catch (error) {
      console.error("Error fetching plan runs:", error);
      showToast("Failed to load plan runs", "", "error");
    } finally {
      setIsLoadingPlanRuns(false);
    }
  };

  // Handle run deleted callback
  const handleRunDeleted = (runId: string) => {
    // Refresh the scheduled job data after a run is deleted
    if (currentPlanHeader?.id) {
      handleShowPlanRuns();
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (originalNodeMap.size > 0) {
      // Restore original state
      const newNodeMap = new Map(Array.from(originalNodeMap.entries()).map(([key, value]) => [key, lodash.cloneDeep(value)]));
      setNodeMap(newNodeMap);
      setQuestions([...originalQuestions]);
      
      // Restore the plan from the root node in the original nodeMap
      const rootNode = Array.from(originalNodeMap.values()).find(node => !node.parent_node_id);
      if (rootNode) {
        setPlan(lodash.cloneDeep(rootNode));
      }
      
      setIsPlanChanged(false);
      showToast("Changes discarded successfully", "", "info");
    }
  };

  // Save the plan.
  const handleSavePlan = async () => {
    const response = await saveKGAnalysisPlanHandler();
    if (response) {
      const planList = await searchPlansheaders(
        -1 /* pageSize*/, 
        1 /*pageNumber*/, 
        "LAST_MODIFIED" /*sortByField*/, 
        false /*sortAscending*/, 
        "" /*searchString*/, 
        [] /*metricIds*/,
        true /*includeScheduleActivity*/,
        "" /*activeSchedule*/
      );
      // If the plan is not in the list, add it to the beginning.
      if (!planHeaders.some(header => header.id === response?.header?.id)) {
        setPlanHeaders(planList?.headers || []);
      } 
      setSelectedPlanHeaders([response?.header!]);
      setCurrentPlanHeader(response?.header!);
      if(isCreatingNewPlan){
        navigate(`/plan/${response?.header?.id}`, {replace: true});
      }
      setIsCreatingNewPlan(false);
      
      // Reset change tracking after successful save
      if (plan && nodeMap.size > 0) {
        setOriginalNodeMap(new Map(Array.from(nodeMap.entries()).map(([key, value]) => [key, lodash.cloneDeep(value)])));
        setOriginalQuestions([...questions]);
        setIsPlanChanged(false);
      }
    }
  };

  // Handle node click to open configurator
  const handleNodeClick = async (_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
    setObservationData(null);
    setShowRecommendedFilters(false);
    // Process recommendation edits.
    await populateRecommendationEdits(node.id, nodeMap);
    configurator.onOpen();
  };

  // Handle select all metrics
  const handleSelectAll = () => {
    // If all metrics are selected, deselect all
    const allMetricIds = new Set(metricList?.map((m) => m?.id || ""));
    const selectedFilteredCount = selectedMetricHeader?.filter((h) =>
      allMetricIds.has(h.id || "")
    ).length;

    if (selectedFilteredCount === metricList.length) {
      // Deselect all metrics
      setSelectedMetricHeader([]);
      setPlan(null);
      setNodeMap(new Map());
      
      // Clear original state if it's a new plan being created
      if (isCreatingNewPlan) {
        setOriginalNodeMap(new Map());
        setOriginalQuestions([]);
        setIsPlanChanged(false);
      }
    } else {
      // Select all metrics
      const metricsToSelect = metricList?.map((m) => m?.id)
        .filter((id): id is string => id !== undefined);

      const metricsToFetch = metricsToSelect?.filter((id) => !metrics?.has(id));

      // Fetch any metrics we don't have data for yet
      const fetchAllMetrics = async () => {
        setIsLoading(true);

        const fetchPromises = metricsToFetch?.map((id) => fetchMetric(id));
        await Promise.all(fetchPromises);

        // Now get all the metric data objects
        const selectedMetricData = metricsToSelect
          ?.map((id) => metrics.get(id))
          .filter(Boolean) as MetricProto[];
        
        // Update selected headers
        setSelectedMetricHeader(metricList);

        // Update the plan
        if (selectedMetricData.length > 0) {
          const rootNode = createRootNode(selectedMetricData);
          setPlan(rootNode);
          const newNodeMap = new Map<string, StepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
          
          // Store original state if it's a new plan being created
          if (isCreatingNewPlan) {
            setOriginalNodeMap(new Map([[rootNode.id, lodash.cloneDeep(rootNode)]]));
            setOriginalQuestions([]);
            setIsPlanChanged(false);
          }
        }

        setIsLoading(false);
      };
      fetchAllMetrics();
    }
  };

  // Handle clear all selections
  const handleClearSelections = () => {
    setSelectedMetricHeader([]);
    setPlan(null);
    setNodeMap(new Map());
    
    // Clear original state if it's a new plan being created
    if (isCreatingNewPlan) {
      setOriginalNodeMap(new Map());
      setOriginalQuestions([]);
      setIsPlanChanged(false);
    }
  };

  // Handle plan name edit
  const handleNameEdit = () => {
    if (!currentPlanHeader?.id) return;
    setIsEditingName(true);
    setEditingName(currentPlanHeader?.name || selectedPlanHeaders[0]?.name || "");
  };

  const handleNameSave = async () => {
    if (currentPlanHeader?.id && editingName.trim()) {
      if (currentPlanHeader?.created_date_ms) {
        // Plan already exists, update via API
        await updateHeader(currentPlanHeader.id, editingName.trim());
        setSelectedPlanHeaders([{...selectedPlanHeaders[0], name: editingName.trim()}]);
        
        // Update the planHeaders state to reflect the change in the left-side list
        const updatedPlanHeaders = planHeaders.map(header => 
          header.id === currentPlanHeader.id 
            ? { ...header, name: editingName.trim() }
            : header
        );
        setPlanHeaders(updatedPlanHeaders);
      } else {
        // New plan, just update local state
        const updatedHeader = {
          ...currentPlanHeader,
          name: editingName.trim()
        };
        setCurrentPlanHeader(updatedHeader);
      }
      setIsEditingName(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleNameSave();
    } else if (e.key === "Escape") {
      setIsEditingName(false);
      setEditingName("");
    }
  };

  // Handle description save - passed to PlanDescriptionComponent
  const handleDescriptionSave = async (description: string) => {
    if (currentPlanHeader?.id) {
      if (currentPlanHeader?.created_date_ms) {
        // Plan already exists, update via API
        const updatedHeader = {
          ...currentPlanHeader,
          description: description
        };
        await updateHeader(currentPlanHeader.id, currentPlanHeader?.name || "", description);
        setCurrentPlanHeader(updatedHeader);
        
        // Update the planHeaders state to reflect the description change in the left-side list
        const updatedPlanHeaders = planHeaders.map(header => 
          header.id === currentPlanHeader.id 
            ? { ...header, description: description }
            : header
        );
        setPlanHeaders(updatedPlanHeaders);
        
        showToast("Plan description updated successfully", "", "success");
      } else {
        // New plan, just update local state
        const updatedHeader = {
          ...currentPlanHeader,
          description: description
        };
        setCurrentPlanHeader(updatedHeader);
      }
      onDescriptionModalClose();
    }
  };

  // handle tab change
  const handleTabChange = (index: number) => {
    setTabIndex(index);
    if(index === 1){
      navigate(`/plan/${id}/insights`);
      if(configurator.isOpen){
        configurator.onClose();
      }
    } else if(index === 2){
      navigate(`/plan/${id}/runs`);
      if(configurator.isOpen){
        configurator.onClose();
      }
    }else if(index === 3){
      navigate(`/plan/${id}/schedule`);
      if(configurator.isOpen){
        configurator.onClose();
      }
    }
    else{
      navigate(`/plan/${id}`);
    }
  };
  
  return (
    <Box width="100%" height="100%" ref={containerRef}>
      <Flex direction="row" height="100%">
        {/* Plan Header List - 1/4 width */}
        <Box
          width={isSidebarVisible ? "27%" : "0%"}
          minWidth={isSidebarVisible ? "300px" : "0px"}
          overflow="hidden"
          overflowY="auto"
          transform={isSidebarVisible ? "translateX(0)" : "translateX(-100%)"}
          transition="all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
          opacity={isSidebarVisible ? 1 : 0}
        >
        <Box
         >
          <ListPlans
            planHeaders={planHeaders}
            setPlanHeaders={setPlanHeaders}
            selectedPlanHeaders={selectedPlanHeaders}
            setSelectedPlanHeaders={setSelectedPlanHeaders}
            onPlanSelection={handlePlanSelection}
            onCreateNewPlan={handleCreateNewPlan}
            toggleSidebar={toggleSidebar}
          />
        </Box>
        </Box>
        {/* Main Content - 3/4 width */}
        <Box width={isSidebarVisible ? "73%" : "100%"} flex="1">

          {currentPlanHeader?.id || isCreatingNewPlan ? (
            <Flex direction="column" height="100%">
              {/* Plan Header Section - Always visible above tabs */}
              <HStack
                spacing={4}
                p={1}
                bg="white"
                borderBottom="1px"
                borderColor="gray.200"
                align="center"
                overflow="visible"
              >
              {!isSidebarVisible && <IconButton
                  aria-label="Collapse Sidebar"
                  icon={<Icon as={TbLayoutSidebarRightCollapseFilled} boxSize={7} />}
                  variant="ghost"
                  size="xs"
                  color="gray.500"
                  _hover={{ color: "purple.600", bg: "gray.50" }}
                  cursor="pointer"
                  m={0}
                  p={0}
                  onClick={toggleSidebar}
              />}
                {/* Plan Info - Left Side */}
                <HStack spacing={0} align="flex-start" w="100%" flexShrink={1}>
                  {/* Plan Name */}
                  {isEditingName ? (
                    <HStack spacing={1}>
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={handleNameSave}
                        onKeyDown={handleNameKeyDown}
                        fontSize="md"
                        fontWeight={500}
                        size="sm"
                        autoFocus
                      />
                      <IconButton
                        size="xs"
                        icon={<CheckIcon boxSize={4} color="black" ml={1}/>}
                        onClick={handleNameSave}
                        aria-label="Save name"
                        borderRadius="full"
                        boxSize={3}
                      />
                    </HStack>
                  ) : (
                      <HStack spacing={1} textAlign="left">
                      <Text
                        fontSize="md"
                        fontWeight={600}
                        color={currentPlanHeader?.id ? "fg.default" : "gray.400"}
                        cursor={currentPlanHeader?.id ? "pointer" : "default"}
                        onClick={currentPlanHeader?.id ? handleNameEdit : undefined}
                        _hover={currentPlanHeader?.id ? { bg: "gray.50" } : {}}
                        borderRadius="sm"
                        mb={0}
                        noOfLines={1}
                        title={currentPlanHeader?.name || selectedPlanHeaders[0]?.name || "No Plan Selected"}
                        ml={isSidebarVisible ? 2 : 0}
                      >
                        {currentPlanHeader?.name || selectedPlanHeaders[0]?.name || "No Plan Selected"}
                      </Text>
                      {currentPlanHeader?.id && 
                        <IconButton
                          size="sm"
                          icon={<EditIcon boxSize={4} />}
                          onClick={handleNameEdit}
                          aria-label="Edit name"
                          variant="ghost"
                          isDisabled={!currentPlanHeader?.id}
                          boxSize={4}
                        />
                      }
                      </HStack>
                  )}
                  <Tooltip 
                    label={currentPlanHeader?.description || "No description available"}
                    placement="right"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    fontSize="sm"
                    p={2}
                    borderRadius="md"
                    maxW="300px"
                    textAlign="center"
                    isDisabled={!currentPlanHeader?.description}
                  >
                   {/* Description Info Button */}
                   <IconButton
                    aria-label="View/Edit Description"
                    icon={<BsFillInfoSquareFill />}
                    size="xs"
                    variant="ghost"
                    color="blue.500"
                    _hover={{ color: "purple.600", bg: "gray.50" }}
                    isDisabled={!currentPlanHeader?.id}
                    onClick={handleDescriptionModalOpen}
                    boxSize={5}
                    alignSelf="center"
                  />
                  </Tooltip>
                   {/* Description Modal */}
                    <CustomModal
                      isOpen={isDescriptionModalOpen}
                      onClose={handleDescriptionModalClose}
                      title="Plan Description"
                      showCloseButton={true}
                      maxHeight="60vh"
                      maxWidth="40vw"
                      isCentered={false}
                    >
                      <PlanDescriptionComponent
                        currentPlanHeader={currentPlanHeader}
                        onSave={handleDescriptionSave}
                        isLoading={isLoading}
                        isExecutingPlan={isExecutingPlan}
                        isFetchingPlan={isFetchingPlan}
                      />
                    </CustomModal>
                </HStack>

                {/* Action Buttons - Right Side */}
                <HStack spacing={2} flexShrink={0}>
                  {isPlanChanged && (
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={handleDiscardChanges}
                      isDisabled={isLoading || isExecutingPlan || isFetchingPlan}
                      size="sm"
                      leftIcon={<Icon as={FaTimes} boxSize={4}/>}
                    >
                      Discard Changes
                    </Button>
                  )}

                  <Button
                    colorScheme="purple"
                    variant="outline"
                    onClick={handleRunJob}
                    isDisabled={
                    isLoading ||
                    isExecutingPlan ||
                    isFetchingPlan ||
                    !currentPlanHeader?.created_date_ms
                    }
                    isLoading={isExecutingPlan}
                    loadingText="Executing..."
                    size="sm"
                    leftIcon={<Icon as={FaPlay} boxSize={3}/>}
                  >
                    Run
                  </Button>
                  <Button
                    colorScheme="green"
                    variant="outline"
                    onClick={handleSavePlan}
                    isDisabled={isLoading || isExecutingPlan || isFetchingPlan}
                    size="sm"
                    leftIcon={<Icon as={FaSave} boxSize={4}/>}
                  >
                    Save 
                  </Button>
                </HStack>

              </HStack>

              {/* Tabs Section */}
              <Tabs 
                flex="1"
                display="flex" 
                flexDirection="column"
                defaultIndex={0}
                index={tabIndex}
                onChange={handleTabChange}
                colorScheme="purple" 
                isFitted 
                variant="line" 
                size="sm"
                height="100%"
              >
                <TabList 
                >
                <Tab 
                  _hover={{
                    bg: "purple.50",
                  }}
                >
                <Icon as={LuWorkflow} boxSize={5} color="fg.default" ml={2} mb={0} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={0}>
                    Workflow
                </Text>
                  </Tab>
                  <Tab 
                  _hover={{
                    bg: "purple.50",
                  }}
                    isDisabled={isCreatingNewPlan || !selectedPlanHeaders.length} 
                  >
                   <Icon as={FaRegLightbulb} boxSize={5} color="fg.default" ml={2} mb={0} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={0}>
                    Questions
                </Text>
                  </Tab>
                  <Tab 
                  _hover={{
                    bg: "purple.50",
                  }}
                    isDisabled={isCreatingNewPlan || !selectedPlanHeaders.length}
                  >
                    <Icon as={FaHistory} boxSize={5} color="fg.default" ml={2} mb={0} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={0}>
                    Past Runs
                </Text>
                  </Tab>
                  <Tab 
                  _hover={{
                    bg: "purple.50",
                  }}
                    isDisabled={isCreatingNewPlan || !selectedPlanHeaders.length}
                  >
                    <Icon as={MdSchedule} boxSize={5} color="fg.default" ml={2} mb={0} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={0}>
                    Schedule
                </Text>
                  </Tab>
                </TabList>

                  <TabPanels flex="1" height="100%">
                  {/* Steps Tab */}
                  <TabPanel p={0} height="100%">
                    <Box flex="1" position="relative" height="100%">
                      {isFetchingPlan ? (
                        <Center height="100%">
                          <VStack spacing={4}>
                            <Spinner size="lg" color="purple.500" />
                            <Text color="gray.600" fontSize="md">Loading plan...</Text>
                          </VStack>
                        </Center>
                      ) : (
                        <ReactFlow
                          nodes={nodes}
                          edges={edges}
                          onNodesChange={onNodesChange}
                          onEdgesChange={onEdgesChange}
                          nodeTypes={{ customNode: AnalysisStepNode }}
                          onEdgeMouseEnter={(_, edge) => setHoveredEdgeId(edge.id as string)}
                          onEdgeMouseLeave={() => setHoveredEdgeId(null)}
                          onNodeClick={handleNodeClick}
                          fitView={false}
                          proOptions={{ hideAttribution: true }}
                          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <Background />
                        </ReactFlow>
                      )}
                    </Box>
                    {/* Data Table Section - Bottom Panel */}
                    {observationData && (
                      <Box
                        position="absolute"
                        bottom={5}
                        left={5}
                        right={5}
                        maxWidth="95%"
                        width="auto"
                        maxHeight="40vh"
                        height="auto"
                        zIndex={10}
                        bg="white"
                        borderRadius="md"
                        boxShadow="lg"
                        display="flex"
                        flexDirection="column"
                      >
                        <DataTableComponent
                          observationData={observationData}
                          metrics={metrics}
                        />
                      </Box>
                    )}
                  </TabPanel>

                  {/* Insights Tab */}
                  <TabPanel p={4} height="100%">
                      <Box height="100%">
                        {isFetchingPlan ? (
                          <Center height="100%">
                            <VStack spacing={4}>
                              <Spinner size="lg" color="purple.500" />
                              <Text color="gray.600" fontSize="md">Loading plan...</Text>
                            </VStack>
                          </Center>
                        ) : (
                          <PlanQNA
                            questions={questions}
                            setQuestions={setQuestions}
                          />
                        )}
                      </Box>
                  </TabPanel>

                  {/* Runs Tab */}
                  <TabPanel p={4} height="100%">
                      <Box height="100%">
                          {isLoadingPlanRuns ? (
                            <Center height="100%">
                              <Spinner size="lg" />
                              <Text ml={4}>Loading agent runs...</Text>
                            </Center>
                          ) : scheduledJobData ? (
                          <JobRuns 
                            scheduledJob={scheduledJobData} 
                            scheduleJobRuns={scheduledJobData.job_runs || []} 
                            onRunDeleted={handleRunDeleted}
                          />
                      ) : (
                          <Center height="100%" bg="white">
                              <Text color="gray.500" fontSize="lg">
                                  No agent runs available
                              </Text>
                          </Center>
                      )}
                      </Box>
                  </TabPanel>
                  {/* Schedule Tab */}
                  <TabPanel p={4} height="100%">
                    <Box height="100%">
                      <Box 
                        mt={2} 
                        border="1px" 
                        borderColor="gray.200" 
                        borderRadius="md" 
                        overflow="hidden"
                      >
                        <ScheduledJob jobHeader={currentPlanHeader} />
                      </Box>
                    </Box>
                  </TabPanel>
                  </TabPanels>
              </Tabs>
            </Flex>
          ) : (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
              <Text fontSize="lg" color="gray.500">
                No plan selected
              </Text>
            </Box>
          )}
        </Box>
      </Flex>
      {/* Node Configurator */}
      <NodeConfigurator />
      
      {/* Metrics Selection Modal */}
      <Modal 
        isOpen={isMetricsSelectionOpen} 
        onClose={onMetricsSelectionClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Select Metrics for Your Plan
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Choose the metrics you want to analyze in your plan: <strong>{currentPlanHeader?.name}</strong>
              </Text>
              
              {/* Metrics Selection Component */}
              <Box>
                <MetricsSelection
                  metricList={metricList}
                  selectedMetricHeader={selectedMetricHeader}
                  onMetricChange={handleMetricChange}
                  onSelectAll={handleSelectAll}
                  onClearSelections={handleClearSelections}
                  isLoading={isLoading}
                  isDisabled={isExecutingPlan}
                  width="100%"
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={() => {
                // Reset selections and close modal
                setSelectedMetricHeader([]);
                setPlan(null);
                setNodeMap(new Map());
                setIsCreatingNewPlan(false);
                setCurrentPlanHeader(undefined);
                onMetricsSelectionClose();
                navigate("/plan");
              }}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={() => {
                if (selectedMetricHeader?.length > 0) {
                  onMetricsSelectionClose();
                  showToast(`${selectedMetricHeader.length} metric(s) selected for your plan`, "", "success");
                } else {
                  showToast("Please select at least one metric", "", "warning");
                }
              }}
              isDisabled={selectedMetricHeader?.length === 0}
            >
              Continue with Selected Metrics
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AnalysisPlan;